import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Calendar, ChevronDown, ChevronLeft, ChevronRight } from 'lucide-react';
import dayjs from 'dayjs';
import quarterOfYear from 'dayjs/plugin/quarterOfYear';
import type { ComparePeriod, PeriodValue } from './schemas';

dayjs.extend(quarterOfYear);

type Props = {
  value: PeriodValue;
  onChange: (next: PeriodValue) => void;
};

// ─── Presets ─────────────────────────────────────────────────────────────────
type PresetDef = { id: string; label: string };

const PRESETS: PresetDef[] = [
  { id: 'custom',    label: 'Personnalisée'             },
  { id: 'today',     label: "Aujourd'hui"               },
  { id: 'yesterday', label: 'Hier'                      },
  { id: '7d',        label: 'Les 7 derniers jours'      },
  { id: '28d',       label: 'Les 28 derniers jours'     },
  { id: '30d',       label: 'Les 30 derniers jours'     },
  { id: 'month',     label: 'Ce mois-ci'                },
  { id: 'prev_month',label: 'Le mois dernier'           },
  { id: '90d',       label: 'Les 90 derniers jours'     },
  { id: 'quarter',   label: 'Le dernier trimestre'      },
  { id: 'ytd',       label: 'Cette année (depuis janvier)' },
  { id: 'last_year', label: 'Dernière année civile'     },
];

function presetToValue(id: string): PeriodValue {
  const today = dayjs();
  // Convention GA4 : les "N derniers jours" excluent aujourd'hui (journée incomplète) et
  // finissent HIER. Les chiffres collent alors à l'UI GA4 au chiffre près.
  const yEnd = today.subtract(1, 'day').format('YYYY-MM-DD');
  switch (id) {
    case 'today':     return { start: today.format('YYYY-MM-DD'), end: today.format('YYYY-MM-DD'), mode: 'duration' };
    case 'yesterday': { const y = today.subtract(1, 'day'); return { start: y.format('YYYY-MM-DD'), end: y.format('YYYY-MM-DD'), mode: 'duration' }; }
    case '7d':        return { start: today.subtract(7, 'day').format('YYYY-MM-DD'), end: yEnd, mode: 'duration' };
    case '28d':       return { start: today.subtract(28, 'day').format('YYYY-MM-DD'), end: yEnd, mode: 'duration' };
    case '30d':       return { start: today.subtract(30, 'day').format('YYYY-MM-DD'), end: yEnd, mode: 'duration' };
    case 'month':     return { start: today.startOf('month').format('YYYY-MM-DD'), end: today.endOf('month').format('YYYY-MM-DD'), mode: 'previous_month' };
    case 'prev_month':{ const p = today.subtract(1, 'month'); return { start: p.startOf('month').format('YYYY-MM-DD'), end: p.endOf('month').format('YYYY-MM-DD'), mode: 'previous_month' }; }
    case '90d':       return { start: today.subtract(90, 'day').format('YYYY-MM-DD'), end: yEnd, mode: 'duration' };
    case 'quarter':   { const q = today.subtract(1, 'quarter'); return { start: q.startOf('quarter').format('YYYY-MM-DD'), end: q.endOf('quarter').format('YYYY-MM-DD'), mode: 'duration' }; }
    case 'ytd':       return { start: today.startOf('year').format('YYYY-MM-DD'), end: yEnd, mode: 'duration' };
    case 'last_year': { const ly = today.subtract(1, 'year'); return { start: ly.startOf('year').format('YYYY-MM-DD'), end: ly.endOf('year').format('YYYY-MM-DD'), mode: 'duration' }; }
    default:          return { start: today.subtract(30, 'day').format('YYYY-MM-DD'), end: yEnd, mode: 'duration' };
  }
}

function valueToPreset(v: PeriodValue): string {
  for (const p of PRESETS) {
    if (p.id === 'custom') continue;
    const candidate = presetToValue(p.id);
    if (candidate.start === v.start && candidate.end === v.end) return p.id;
  }
  return 'custom';
}

function computePreviousPeriod(start: string, end: string): { start: string; end: string } {
  const s = dayjs(start);
  const e = dayjs(end);
  const days = e.diff(s, 'day') + 1;
  const cEnd = s.subtract(1, 'day');
  const cStart = cEnd.subtract(days - 1, 'day');
  return { start: cStart.format('YYYY-MM-DD'), end: cEnd.format('YYYY-MM-DD') };
}

function computePreviousYear(start: string, end: string): { start: string; end: string } {
  return {
    start: dayjs(start).subtract(1, 'year').format('YYYY-MM-DD'),
    end:   dayjs(end).subtract(1, 'year').format('YYYY-MM-DD'),
  };
}

function getCompareDates(preset: ComparePeriod['preset'], mainStart: string, mainEnd: string, customDraft: { start: string; end: string }): { start: string; end: string } {
  if (preset === 'previous_period') return computePreviousPeriod(mainStart, mainEnd);
  if (preset === 'previous_year')   return computePreviousYear(mainStart, mainEnd);
  return customDraft;
}

// ─── Calendar helpers ─────────────────────────────────────────────────────────
const DAYS  = ['L', 'M', 'M', 'J', 'V', 'S', 'D'];
const MONTH_NAMES = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
  'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];

function getDaysInMonth(year: number, month: number) { return new Date(year, month + 1, 0).getDate(); }
function getFirstWeekday(year: number, month: number) { return (new Date(year, month, 1).getDay() + 6) % 7; }

function buildCells(year: number, month: number): (string | null)[] {
  const days = getDaysInMonth(year, month);
  const offset = getFirstWeekday(year, month);
  const cells: (string | null)[] = Array(offset).fill(null);
  for (let d = 1; d <= days; d++) {
    cells.push(`${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`);
  }
  return cells;
}

function addMonths(year: number, month: number, delta: number) {
  const d = new Date(year, month + delta, 1);
  return { year: d.getFullYear(), month: d.getMonth() };
}

function fmt(d: string) {
  const [y, m, day] = d.split('-');
  return `${parseInt(day, 10)} ${MONTH_NAMES[parseInt(m, 10) - 1]} ${y}`;
}

// ISO YYYY-MM-DD → DD/MM/YYYY
function isoToFr(d: string): string {
  if (!d) return '';
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(d);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : d;
}

// DD/MM/YYYY → YYYY-MM-DD (partial input returns '' if invalid)
function frToIso(d: string): string | null {
  const m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(d);
  if (!m) return null;
  const [, dd, mm, yyyy] = m;
  const day = parseInt(dd, 10);
  const mon = parseInt(mm, 10);
  if (mon < 1 || mon > 12 || day < 1 || day > 31) return null;
  return `${yyyy}-${mm}-${dd}`;
}

// ─── One month grid ───────────────────────────────────────────────────────────
function MonthGrid({
  year, month, start, end, hover, picking,
  onDayClick, onDayHover,
}: {
  year: number; month: number;
  start: string; end: string; hover: string | null; picking: 'start' | 'end';
  onDayClick: (d: string) => void;
  onDayHover: (d: string | null) => void;
}) {
  const cells = buildCells(year, month);
  const today = dayjs().format('YYYY-MM-DD');
  const effectiveEnd = picking === 'end' && hover ? hover : end;

  return (
    <div className="w-[220px] flex-shrink-0">
      <div className="text-center text-sm font-bold text-[#1B2559] mb-2 uppercase tracking-wide">
        {MONTH_NAMES[month]} {year}
      </div>
      <div className="grid grid-cols-7 mb-1">
        {DAYS.map((d, i) => (
          <div key={i} className="text-center text-[10px] text-[#8B8FA7] font-medium py-0.5">{d}</div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-px">
        {cells.map((dateStr, i) => {
          if (!dateStr) return <div key={`e${i}`} />;
          const isStart   = dateStr === start;
          const isEnd     = dateStr === effectiveEnd;
          const inRange   = start && effectiveEnd && dateStr > start && dateStr < effectiveEnd;
          const isToday   = dateStr === today;
          const isFuture  = dateStr > today;

          return (
            <button
              key={dateStr}
              type="button"
              disabled={isFuture}
              onClick={() => onDayClick(dateStr)}
              onMouseEnter={() => onDayHover(dateStr)}
              onMouseLeave={() => onDayHover(null)}
              className={[
                'relative aspect-square flex items-center justify-center text-[11px] rounded-lg transition-colors select-none',
                isFuture ? 'text-[#8B8FA7] cursor-default' :
                (isStart || isEnd)
                  ? 'bg-[#6366F1] text-white font-semibold z-10'
                  : inRange
                    ? 'bg-[#6366F1]/15 text-[#6366F1]'
                    : 'hover:bg-[#F4F5FA] text-[#555A73]',
                isToday && !(isStart || isEnd) ? 'ring-2 ring-[#6366F1]/60 ring-inset' : '',
              ].join(' ')}
            >
              {parseInt(dateStr.split('-')[2], 10)}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────
export default function PeriodPicker({ value, onChange }: Props) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState({ start: value.start, end: value.end });
  const [picking, setPicking] = useState<'start' | 'end'>('start');
  const [hover, setHover] = useState<string | null>(null);
  const [viewYear, setViewYear] = useState(() => {
    const d = dayjs(value.end || value.start);
    return d.year();
  });
  const [viewMonth, setViewMonth] = useState(() => {
    const d = dayjs(value.end || value.start);
    return Math.max(0, d.month() - 2);
  });
  const [activePreset, setActivePreset] = useState(() => valueToPreset(value));
  const [startInput, setStartInput] = useState(() => isoToFr(value.start));
  const [endInput, setEndInput]     = useState(() => isoToFr(value.end));

  // Compare state
  const [compareEnabled, setCompareEnabled] = useState(() => !!value.compare);
  const [comparePreset, setComparePreset] = useState<ComparePeriod['preset']>(
    () => value.compare?.preset ?? 'previous_period',
  );
  const [compareDraft, setCompareDraft] = useState(() => {
    if (value.compare) return { start: value.compare.start, end: value.compare.end };
    return computePreviousPeriod(value.start, value.end);
  });
  const [compareStartInput, setCompareStartInput] = useState(() => isoToFr(value.compare?.start ?? ''));
  const [compareEndInput, setCompareEndInput]     = useState(() => isoToFr(value.compare?.end ?? ''));

  const ref = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popupRef = useRef<HTMLDivElement>(null);
  const [popupPos, setPopupPos] = useState<{ top: number; left: number; maxHeight: number } | null>(null);

  useEffect(() => {
    if (!open) return;
    function handler(e: MouseEvent) {
      if (!ref.current?.contains(e.target as Node) && !popupRef.current?.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // Position popup: always 10px below the trigger, right-anchored, clamped to viewport
  useEffect(() => {
    if (!open || !triggerRef.current) return;
    function reposition() {
      const trig = triggerRef.current?.getBoundingClientRect();
      if (!trig) return;
      const gutter = 12;
      const offset = 10;
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      // Use measured popup width if available, otherwise estimate
      const popupW = popupRef.current?.offsetWidth || 920;

      // Horizontal: anchor popup's right edge to trigger's right edge, clamp into viewport
      let left = trig.right - popupW;
      if (left < gutter) left = gutter;
      if (left + popupW > vw - gutter) left = vw - gutter - popupW;

      const top = trig.bottom + offset;
      const maxHeight = vh - top - gutter;
      setPopupPos({ top, left, maxHeight });
    }
    reposition();
    // Re-measure after render so we get the actual popup width
    const raf = requestAnimationFrame(reposition);
    window.addEventListener('resize', reposition);
    window.addEventListener('scroll', reposition, true);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', reposition);
      window.removeEventListener('scroll', reposition, true);
    };
  }, [open]);

  // Sync draft when value changes externally
  useEffect(() => {
    setDraft({ start: value.start, end: value.end });
    setStartInput(isoToFr(value.start));
    setEndInput(isoToFr(value.end));
    setActivePreset(valueToPreset(value));
    setCompareEnabled(!!value.compare);
    if (value.compare) {
      setComparePreset(value.compare.preset);
      setCompareDraft({ start: value.compare.start, end: value.compare.end });
    }
  }, [value.start, value.end]);

  // Recompute auto compare dates when main draft changes
  useEffect(() => {
    if (!compareEnabled || comparePreset === 'custom') return;
    const computed = getCompareDates(comparePreset, draft.start, draft.end, compareDraft);
    setCompareDraft(computed);
    setCompareStartInput(isoToFr(computed.start));
    setCompareEndInput(isoToFr(computed.end));
  }, [draft.start, draft.end, comparePreset, compareEnabled]);

  function openPicker() {
    setDraft({ start: value.start, end: value.end });
    setStartInput(isoToFr(value.start));
    setEndInput(isoToFr(value.end));
    setPicking('start');
    const d = dayjs(value.end || value.start);
    const m = Math.max(0, d.month() - 2);
    setViewMonth(m < 0 ? 10 : m);
    setViewYear(m < 0 ? d.year() - 1 : d.year());
    setCompareEnabled(!!value.compare);
    if (value.compare) {
      setComparePreset(value.compare.preset);
      setCompareDraft({ start: value.compare.start, end: value.compare.end });
    }
    setOpen(true);
  }

  function handlePresetClick(id: string) {
    setActivePreset(id);
    if (id === 'custom') return;
    const v = presetToValue(id);
    setDraft({ start: v.start, end: v.end });
    setStartInput(isoToFr(v.start));
    setEndInput(isoToFr(v.end));
    setPicking('start');
    const d = dayjs(v.end);
    const m = Math.max(0, d.month() - 2);
    setViewMonth(m);
    setViewYear(d.year());
  }

  function handleDayClick(dateStr: string) {
    if (picking === 'start') {
      setDraft({ start: dateStr, end: dateStr });
      setStartInput(isoToFr(dateStr));
      setEndInput(isoToFr(dateStr));
      setPicking('end');
    } else {
      const newStart = dateStr < draft.start ? dateStr : draft.start;
      const newEnd   = dateStr < draft.start ? draft.start : dateStr;
      setDraft({ start: newStart, end: newEnd });
      setStartInput(isoToFr(newStart));
      setEndInput(isoToFr(newEnd));
      setPicking('start');
      setActivePreset(valueToPreset({ start: newStart, end: newEnd, mode: 'duration' }));
    }
  }

  function handleInputChange(field: 'start' | 'end', v: string) {
    if (field === 'start') setStartInput(v);
    else setEndInput(v);
    const iso = frToIso(v);
    if (iso) {
      const next = { ...draft, [field]: iso };
      if (next.start <= next.end) {
        setDraft(next);
        setActivePreset(valueToPreset({ ...next, mode: 'duration' }));
      }
    }
  }

  function handleComparePresetChange(preset: ComparePeriod['preset']) {
    setComparePreset(preset);
    if (preset !== 'custom') {
      const computed = getCompareDates(preset, draft.start, draft.end, compareDraft);
      setCompareDraft(computed);
      setCompareStartInput(isoToFr(computed.start));
      setCompareEndInput(isoToFr(computed.end));
    }
  }

  function handleCompareInputChange(field: 'start' | 'end', v: string) {
    if (field === 'start') setCompareStartInput(v);
    else setCompareEndInput(v);
    const iso = frToIso(v);
    if (iso) {
      setCompareDraft((prev) => ({ ...prev, [field]: iso }));
    }
  }

  function navMonth(delta: number) {
    const { year, month } = addMonths(viewYear, viewMonth, delta);
    setViewYear(year);
    setViewMonth(month);
  }

  function handleApply() {
    const mode: PeriodValue['mode'] = activePreset === 'month' || activePreset === 'prev_month'
      ? 'previous_month'
      : 'duration';
    const compareDates = compareEnabled
      ? getCompareDates(comparePreset, draft.start, draft.end, compareDraft)
      : null;
    onChange({
      start: draft.start,
      end:   draft.end,
      mode,
      compare: compareEnabled && compareDates
        ? { ...compareDates, preset: comparePreset }
        : null,
    });
    setOpen(false);
  }

  function handleCancel() {
    setDraft({ start: value.start, end: value.end });
    setOpen(false);
  }

  const months = [0, 1, 2].map((offset) => addMonths(viewYear, viewMonth, offset));

  // Display compare dates for the current state
  const displayCompareDates = compareEnabled
    ? getCompareDates(comparePreset, draft.start, draft.end, compareDraft)
    : null;

  return (
    <div className="relative" ref={ref}>
      {/* Trigger */}
      <button
        ref={triggerRef}
        type="button"
        onClick={openPicker}
        className={`flex items-center gap-2 px-3 py-1.5 border rounded-lg text-sm font-medium transition-colors ${
          open
            ? 'border-[#6366F1] bg-[#6366F1]/10 text-[#6366F1]'
            : 'border-[#E2E4ED] bg-white text-[#555A73] hover:border-gray-400'
        }`}
      >
        <Calendar size={14} className="text-[#8B8FA7] flex-shrink-0" />
        <span>
          {value.start && value.end
            ? `${fmt(value.start)} – ${fmt(value.end)}`
            : 'Sélectionner une période'}
        </span>
        {value.compare && (
          <span className="text-[10px] bg-[#6366F1]/10 text-[#6366F1] px-1.5 py-0.5 rounded font-medium flex-shrink-0">
            vs
          </span>
        )}
        <ChevronDown size={13} className="text-[#8B8FA7] flex-shrink-0" />
      </button>

      {/* Dropdown (portaled to body to escape backdrop-filter containing block) */}
      {open && createPortal(
        <div
          ref={popupRef}
          className="fixed z-[1100] bg-white border border-[#E2E4ED] rounded-xl shadow-2xl flex overflow-y-auto"
          style={{
            top: popupPos?.top ?? -9999,
            left: popupPos?.left ?? -9999,
            maxHeight: popupPos?.maxHeight,
            visibility: popupPos ? 'visible' : 'hidden',
          }}
        >
          {/* Sidebar presets */}
          <div className="w-44 border-r border-[#E2E4ED] py-2 flex-shrink-0">
            {PRESETS.map((p) => (
              <button
                key={p.id}
                type="button"
                onClick={() => handlePresetClick(p.id)}
                className={`w-full text-left px-4 py-1.5 text-sm transition-colors ${
                  activePreset === p.id
                    ? 'bg-[#6366F1]/10 text-[#6366F1] font-medium'
                    : 'text-[#555A73] hover:bg-[#F4F5FA]'
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>

          {/* Calendar area */}
          <div className="flex flex-col flex-1">
            {/* Date inputs */}
            <div className="flex items-center gap-4 px-5 pt-4 pb-3 border-b border-[#E2E4ED]">
              <div>
                <div className="text-[10px] uppercase tracking-wider text-[#8B8FA7] font-semibold mb-1">Date de début</div>
                <input
                  type="text"
                  value={startInput}
                  placeholder="JJ/MM/AAAA"
                  onChange={(e) => handleInputChange('start', e.target.value)}
                  onFocus={() => setPicking('start')}
                  className={`w-32 px-2 py-1 border rounded text-sm font-mono ${picking === 'start' ? 'border-[#6366F1] ring-1 ring-[#6366F1]/40' : 'border-[#E2E4ED]'}`}
                />
              </div>
              <span className="text-[#8B8FA7] mt-4">—</span>
              <div>
                <div className="text-[10px] uppercase tracking-wider text-[#8B8FA7] font-semibold mb-1">Date de fin</div>
                <input
                  type="text"
                  value={endInput}
                  placeholder="JJ/MM/AAAA"
                  onChange={(e) => handleInputChange('end', e.target.value)}
                  onFocus={() => setPicking('end')}
                  className={`w-32 px-2 py-1 border rounded text-sm font-mono ${picking === 'end' ? 'border-[#6366F1] ring-1 ring-[#6366F1]/40' : 'border-[#E2E4ED]'}`}
                />
              </div>
            </div>

            {/* 3-month grid */}
            <div className="flex items-start gap-6 px-5 py-4 flex-1 relative">
              <button
                type="button"
                onClick={() => navMonth(-1)}
                className="absolute left-1 top-1/2 -translate-y-1/2 p-1 hover:bg-[#F4F5FA] rounded-lg text-[#555A73]"
              >
                <ChevronLeft size={16} />
              </button>
              <button
                type="button"
                onClick={() => navMonth(1)}
                className="absolute right-1 top-1/2 -translate-y-1/2 p-1 hover:bg-[#F4F5FA] rounded-lg text-[#555A73]"
              >
                <ChevronRight size={16} />
              </button>

              {months.map(({ year, month }) => (
                <MonthGrid
                  key={`${year}-${month}`}
                  year={year}
                  month={month}
                  start={draft.start}
                  end={draft.end}
                  hover={hover}
                  picking={picking}
                  onDayClick={handleDayClick}
                  onDayHover={setHover}
                />
              ))}
            </div>

            {/* Compare row */}
            <div className="px-5 py-3 border-t border-[#E2E4ED] bg-[#F4F5FA] flex items-center gap-4">
              {/* Toggle */}
              <button
                type="button"
                onClick={() => setCompareEnabled((v) => !v)}
                className="flex items-center gap-2 text-sm font-medium text-[#555A73] flex-shrink-0"
              >
                <span className={`relative inline-flex h-4 w-7 flex-shrink-0 items-center rounded-full transition-colors ${compareEnabled ? 'bg-[#6366F1]' : 'bg-gray-300'}`}>
                  <span className={`inline-block h-3 w-3 transform rounded-full bg-white shadow transition-transform ${compareEnabled ? 'translate-x-3.5' : 'translate-x-0.5'}`} />
                </span>
                Comparer
              </button>

              {compareEnabled && (
                <>
                  {/* Preset buttons */}
                  <div className="flex rounded border border-[#E2E4ED] overflow-hidden flex-shrink-0">
                    {([
                      { id: 'previous_period', label: 'Pér. précédente' },
                      { id: 'previous_year',   label: 'Année précédente' },
                      { id: 'custom',          label: 'Personnalisée' },
                    ] as { id: ComparePeriod['preset']; label: string }[]).map(({ id, label }) => (
                      <button
                        key={id}
                        type="button"
                        onClick={() => handleComparePresetChange(id)}
                        className={`px-2.5 py-1 text-xs ${comparePreset === id ? 'bg-[#6366F1] text-white font-medium' : 'text-[#555A73] hover:bg-[#F4F5FA]'}`}
                      >
                        {label}
                      </button>
                    ))}
                  </div>

                  {/* Date display / inputs for custom */}
                  {comparePreset === 'custom' ? (
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        value={compareStartInput}
                        placeholder="JJ/MM/AAAA"
                        onChange={(e) => handleCompareInputChange('start', e.target.value)}
                        className="w-28 px-2 py-0.5 border border-[#E2E4ED] rounded text-xs font-mono"
                      />
                      <span className="text-[#8B8FA7] text-xs">—</span>
                      <input
                        type="text"
                        value={compareEndInput}
                        placeholder="JJ/MM/AAAA"
                        onChange={(e) => handleCompareInputChange('end', e.target.value)}
                        className="w-28 px-2 py-0.5 border border-[#E2E4ED] rounded text-xs font-mono"
                      />
                    </div>
                  ) : (
                    displayCompareDates && (
                      <span className="text-xs text-[#6366F1] font-medium">
                        {fmt(displayCompareDates.start)} – {fmt(displayCompareDates.end)}
                      </span>
                    )
                  )}
                </>
              )}
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-[#E2E4ED] bg-[#F4F5FA]">
              <button
                type="button"
                onClick={handleCancel}
                className="px-4 py-1.5 text-sm text-[#555A73] hover:text-[#1B2559]"
              >
                Annuler
              </button>
              <button
                type="button"
                onClick={handleApply}
                disabled={!draft.start || !draft.end}
                className="px-5 py-1.5 text-sm bg-[#6366F1] text-white rounded-lg hover:opacity-90 disabled:opacity-50 font-medium"
              >
                Appliquer
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
